import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { fetchActiveSessions, revokeSession } from "@admin/services/adminApi";
import { DataTable, Pill } from "@admin/components/Primitives";
import { Paginator, PAGE_SIZE } from "@admin/components/security/Paginator";
import { AdminPicker, adminParam } from "@admin/components/security/AdminPicker";
import { fmtDt } from "@admin/components/security/format";

export function SessionsSection({ canManageAdmins, meId, onChanged }) {
  const [admin, setAdmin] = useState("me");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ total: 0, items: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await fetchActiveSessions({
        user_id: canManageAdmins && admin !== "me" ? adminParam(admin, meId) : undefined,
        skip, limit: PAGE_SIZE,
      });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load sessions"); }
    setBusy(false);
  }, [admin, skip, canManageAdmins, meId]);
  useEffect(() => { load(); }, [load]);

  const onRevoke = async (tok) => {
    if (!window.confirm("Revoke this session? The user will be signed out.")) return;
    try { await revokeSession(tok); toast.success("Session revoked"); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "created_at", label: "Started", render: (r) => <span className="text-xs">{fmtDt(r.created_at)}</span> },
    { key: "expires_at", label: "Expires", render: (r) => <span className="text-xs">{fmtDt(r.expires_at)}</span> },
    { key: "app", label: "App", render: (r) => <Pill color="slate">{r.app || "—"}</Pill> },
    {
      key: "two_factor_verified_at", label: "2FA",
      render: (r) => r.two_factor_verified_at ? <Pill color="emerald">Verified</Pill> : <Pill color="amber">Not verified</Pill>,
    },
    { key: "session_token", label: "Token", render: (r) => <code className="text-[10px] text-slate-400">{(r.session_token || "").slice(0, 18)}…</code> },
    {
      key: "is_current", label: "",
      render: (r) => r.is_current
        ? <Pill color="gold">This session</Pill>
        : (
          <button onClick={() => onRevoke(r.session_token)} className="px-2 py-1 text-[11px] font-bold rounded-md text-rose-700 border border-rose-200 hover:bg-rose-50" data-testid={`revoke-session-${r.session_token.slice(0, 10)}`}>
            <Trash2 size={10} className="inline mr-1" /> Revoke
          </button>
        ),
    },
  ];

  return (
    <section className="mt-8" data-testid="security-sessions-section">
      <div className="flex flex-wrap justify-between items-end gap-2 mb-3">
        <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400">Active sessions</h2>
        {canManageAdmins && <AdminPicker value={admin} meId={meId} allowAll={false} testid="sessions-filter-admin" onChange={(v) => { setAdmin(v); setSkip(0); }} />}
      </div>
      <DataTable testid="sessions-table" columns={columns} rows={data.items} empty={busy ? "Loading…" : "No active sessions"} />
      <Paginator skip={skip} total={data.total} onChange={setSkip} testid="sessions-pager" busy={busy} />
      <p className="mt-2 text-[11px] text-slate-400">
        Only live sessions are listed (expired and revoked ones are excluded). You can't revoke the session you're currently using — sign out from the sidebar instead.
      </p>
    </section>
  );
}
